import { useCallback, useEffect, useRef, useState } from 'react';
import { previewReminderMessages } from '../../../../api/collections';
import { selectedChannels } from '../utils/collectionReminders';
import { buildReminderPreviewPayload } from '../utils/reminderTemplates';

/** Espera tras un cambio del formulario antes de pedir la vista previa (billing la limita). */
export const REMINDER_PREVIEW_DEBOUNCE_MS = 450;

const EMPTY = { pending: false, data: null, error: null };

/**
 * Vista previa del mensaje exacto de "Enviar recordatorio" y "Programar" (POST
 * billing/collections/reminders/preview): lo que saldría HOY por comercio y canal, con la
 * plantilla elegida. Billing arma el texto; el front no lo reconstruye.
 *
 * Mismo criterio que la vista previa del editor: se pide recién `delay` ms después del último
 * cambio, y cada cambio del pedido deja vieja cualquier respuesta en vuelo (una que llega
 * tarde se descarta). Mientras llega la nueva se conserva la última buena con `pending`.
 *
 * El pedido se compara por VALOR (como texto): los arreglos y objetos del formulario llegan
 * nuevos en cada render y no deben disparar pedidos repetidos.
 *
 * Deshabilitado (formulario cerrado) se vacía: al reabrir no se muestra el mensaje de otra
 * nota. Sin canales o sin notas no pide nada (`idle`).
 *
 * `sendAt` (Programar): billing omite las notas que ya esperan en otro programado cercano
 * por el mismo canal, como al programar.
 *
 * @param {{ noteIds: string[], channels: { email?: boolean, whatsapp?: boolean } | null,
 *   templateIds?: { email?: string|null, whatsapp?: string|null } | null,
 *   companyId?: string|null, limit?: number, sendAt?: { date: string, hour: number } | null,
 *   enabled?: boolean, delay?: number }} params
 * @returns {{ pending: boolean, data: object|null, error: unknown,
 *   idle: 'no_channels'|'no_notes'|null, refresh: () => void }}
 */
export default function useReminderMessagePreview({
  noteIds,
  channels,
  templateIds,
  companyId,
  limit,
  sendAt,
  enabled = true,
  delay = REMINDER_PREVIEW_DEBOUNCE_MS,
}) {
  const [state, setState] = useState(EMPTY);
  const requestSeq = useRef(0);

  let idle = null;
  if (selectedChannels(channels).length === 0) idle = 'no_channels';
  else if (!Array.isArray(noteIds) || noteIds.length === 0) idle = 'no_notes';

  const requestKey =
    enabled && !idle
      ? JSON.stringify(buildReminderPreviewPayload({ noteIds, channels, templateIds, companyId, limit, sendAt }))
      : null;

  const run = useCallback(async (seq, key) => {
    try {
      const { data } = await previewReminderMessages(JSON.parse(key));
      if (seq !== requestSeq.current) return;
      setState({ pending: false, data, error: null });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setState((s) => ({ pending: false, data: s.data, error: err }));
    }
  }, []);

  useEffect(() => {
    // Todo cambio del pedido deja vieja cualquier respuesta en vuelo.
    const seq = ++requestSeq.current;
    if (!enabled) {
      setState(EMPTY);
      return undefined;
    }
    if (!requestKey) {
      setState((s) => (s.pending ? { ...s, pending: false } : s));
      return undefined;
    }
    setState((s) => (s.pending ? s : { ...s, pending: true }));
    const timer = setTimeout(() => run(seq, requestKey), delay);
    return () => clearTimeout(timer);
  }, [enabled, requestKey, run, delay]);

  // Al desmontar, nada de lo que esté en vuelo escribe.
  useEffect(
    () => () => {
      requestSeq.current += 1;
    },
    [],
  );

  const refresh = useCallback(() => {
    if (!requestKey) return;
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, pending: true }));
    run(seq, requestKey);
  }, [requestKey, run]);

  return { ...state, idle, refresh };
}
